import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { FormSkeleton } from "@/components/ui/page-skeletons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  User,
  Building2,
  Briefcase,
  FileText,
  Mail,
  Clock,
  CheckSquare2,
  Phone,
  Smartphone,
  TrendingUp,
  Star,
  ExternalLink,
  FileSignature,
} from "lucide-react";
import { WhatsAppIcon } from "@/components/whatsapp/WhatsAppIcon";
import { WhatsAppChatDialog } from "@/components/whatsapp/WhatsAppChatDialog";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { EntityDetailLayout, type TabItem } from "@/components/entity-detail/EntityDetailLayout";
import { EntityHeader } from "@/components/entity-detail/EntityHeader";
import { EntityInfoSidebar, type InfoSection } from "@/components/entity-detail/EntityInfoSidebar";
import { DealsWidget } from "@/components/entity-detail/DealsWidget";
import { QuotesWidget } from "@/components/entity-detail/QuotesWidget";
import { EmailsWidget } from "@/components/entity-detail/EmailsWidget";
import { WhatsAppWidget } from "@/components/entity-detail/WhatsAppWidget";
import { TasksWidget } from "@/components/entity-detail/TasksWidget";
import { ActivityTimeline } from "@/components/entity-detail/ActivityTimeline";
import { formatINR } from "@/lib/utils";
import type { Contact360 } from "@/types";

export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { success, error: toastError } = useToast();
  const { hasPermission } = useAuth();

  const [data, setData] = useState<Contact360 | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    mobile: "",
    job_title: "",
    is_primary: false,
  });
  const [editLoading, setEditLoading] = useState(false);

  // WhatsApp chat dialog
  const [waDialogOpen, setWaDialogOpen] = useState(false);

  // Task dialog
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);

  const fetch360 = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await api.get(`/api/Contact/360/${id}`);
      const payload = unwrapObject<Contact360>(res);
      setData(payload);
      if (payload?.contact) {
        setEditForm({
          first_name: payload.contact.first_name || "",
          last_name: payload.contact.last_name || "",
          email: payload.contact.email || "",
          phone: payload.contact.phone || "",
          mobile: payload.contact.mobile || "",
          job_title: payload.contact.job_title || "",
          is_primary: Boolean(payload.contact.is_primary),
        });
      }
    } catch (err: any) {
      toastError("Failed to load contact", err.message || "Contact not found");
      navigate("/contacts");
    } finally {
      setLoading(false);
    }
  }, [id, navigate, toastError]);

  useEffect(() => {
    fetch360();
  }, [fetch360]);

  if (loading || !data) {
    return (
      <div className="p-6 space-y-6">
        <FormSkeleton />
      </div>
    );
  }

  const { contact, account, deals, quotations, emails, whatsapp, tasks } = data;
  const history = data.history || [];
  const fullName = `${contact.first_name} ${contact.last_name || ""}`.trim();
  const phoneNum = contact.mobile || contact.phone;

  const totalDealValue = deals.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
  const openTasksCount = tasks.filter((t) => t.task_status === "OPEN").length;

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    try {
      setEditLoading(true);
      await api.put(`/api/Contact/Update/${id}`, {
        ...editForm,
        account_id: contact.account_id,
      });
      success("Contact updated", "Contact details saved successfully.");
      setEditOpen(false);
      fetch360();
    } catch (err: any) {
      toastError("Update failed", err.message || "Could not update contact.");
    } finally {
      setEditLoading(false);
    }
  };

  const sidebarSections: InfoSection[] = [
    {
      title: "Organization",
      fields: [
        {
          icon: Building2,
          label: "Company / Account",
          value: account ? (
            <Link to={`/accounts/${account.id}`} className="text-primary font-semibold hover:underline inline-flex items-center gap-1">
              <span>{account.name}</span>
              <ExternalLink className="size-3 text-muted-foreground" />
            </Link>
          ) : (
            "—"
          ),
        },
        {
          icon: User,
          label: "Job Title",
          value: contact.job_title,
        },
      ],
    },
    {
      title: "Direct Contact",
      fields: [
        {
          icon: Smartphone,
          label: "Mobile / WhatsApp",
          value: contact.mobile,
          href: contact.mobile ? `tel:${contact.mobile}` : undefined,
          copyable: true,
        },
        {
          icon: Phone,
          label: "Office Phone",
          value: contact.phone,
          href: contact.phone ? `tel:${contact.phone}` : undefined,
          copyable: true,
        },
        {
          icon: Mail,
          label: "Email",
          value: contact.email,
          href: contact.email ? `mailto:${contact.email}` : undefined,
          copyable: true,
        },
      ],
    },
  ];

  // Overview Tab Component
  const OverviewTab = (
    <div className="space-y-6">
      {/* Metric Cards Row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div
          onClick={() => setActiveTab("deals")}
          className="bg-card border border-border/80 rounded-2xl p-4 shadow-2xs hover:border-primary/50 transition-colors cursor-pointer space-y-1"
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Deals</span>
            <div className="size-7 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center">
              <Briefcase className="size-3.5" />
            </div>
          </div>
          <div className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">{deals.length}</div>
          <p className="text-[11px] font-mono text-muted-foreground font-semibold">{formatINR(totalDealValue)}</p>
        </div>

        <div
          onClick={() => setActiveTab("quotes")}
          className="bg-card border border-border/80 rounded-2xl p-4 shadow-2xs hover:border-primary/50 transition-colors cursor-pointer space-y-1"
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Quotations</span>
            <div className="size-7 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center">
              <FileText className="size-3.5" />
            </div>
          </div>
          <div className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">{quotations.length}</div>
          <p className="text-[11px] text-muted-foreground">Issued quotes</p>
        </div>

        <div
          onClick={() => setActiveTab("tasks")}
          className="bg-card border border-border/80 rounded-2xl p-4 shadow-2xs hover:border-primary/50 transition-colors cursor-pointer space-y-1"
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Tasks</span>
            <div className="size-7 rounded-lg bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
              <CheckSquare2 className="size-3.5" />
            </div>
          </div>
          <div className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">{openTasksCount}</div>
          <p className="text-[11px] text-muted-foreground">{tasks.length} total tasks</p>
        </div>
      </div>

      {/* Recent Activity Timeline Preview */}
      <ActivityTimeline
        deals={deals}
        quotations={quotations}
        emails={emails}
        whatsapp={whatsapp}
        tasks={tasks}
        history={history}
        onOpenWhatsApp={() => setWaDialogOpen(true)}
      />
    </div>
  );

  const tabs: TabItem[] = [
    { id: "overview", label: "Overview", icon: TrendingUp, content: OverviewTab },
    {
      id: "deals",
      label: "Deals",
      icon: Briefcase,
      count: deals.length,
      content: (
        <DealsWidget
          deals={deals}
          onNewDeal={() =>
            navigate(
              `/deals?create=true&contact_id=${contact.id}&contact_name=${encodeURIComponent(`${contact.first_name} ${contact.last_name || ""}`.trim())}` +
                (contact.account_id ? `&account_id=${contact.account_id}&account_name=${encodeURIComponent(account?.name || "")}` : "")
            )
          }
          canCreate={hasPermission("deal.create")}
        />
      ),
    },
    {
      id: "quotes",
      label: "Quotes",
      icon: FileText,
      count: quotations.length,
      content: (
        <QuotesWidget
          quotes={quotations}
          canCreate={hasPermission("quote.create")}
        />
      ),
    },
    {
      id: "emails",
      label: "Emails",
      icon: Mail,
      count: emails.length,
      content: (
        <EmailsWidget
          emails={emails}
          canSend={hasPermission("email.send")}
        />
      ),
    },
    {
      id: "whatsapp",
      label: "WhatsApp",
      icon: WhatsAppIcon as any,
      count: whatsapp.length,
      content: (
        <WhatsAppWidget
          threads={whatsapp}
          onOpenChat={() => setWaDialogOpen(true)}
          onStartChat={phoneNum ? () => setWaDialogOpen(true) : undefined}
          canSend={hasPermission("whatsapp.send")}
        />
      ),
    },
    {
      id: "tasks",
      label: "Tasks",
      icon: CheckSquare2,
      count: openTasksCount,
      content: (
        <TasksWidget
          tasks={tasks}
          relatedType="CONTACT"
          relatedId={contact.id}
          relatedName={fullName}
          onRefresh={fetch360}
          canCreate={hasPermission("activity.create")}
        />
      ),
    },
    {
      id: "timeline",
      label: "Timeline",
      icon: Clock,
      content: (
        <ActivityTimeline
          deals={deals}
          quotations={quotations}
          emails={emails}
          whatsapp={whatsapp}
          tasks={tasks}
          history={history}
          onOpenWhatsApp={() => setWaDialogOpen(true)}
        />
      ),
    },
  ];

  return (
    <>
      <EntityDetailLayout
        breadcrumbs={[
          { label: "Contacts", href: "/contacts" },
          { label: fullName },
        ]}
        header={
          <EntityHeader
            type="CONTACT"
            title={fullName}
            subtitle={
              <div className="flex items-center gap-1.5 flex-wrap">
                {contact.job_title && <span>{contact.job_title}</span>}
                {account && (
                  <>
                    <span>at</span>
                    <Link to={`/accounts/${account.id}`} className="font-semibold text-primary hover:underline">
                      {account.name}
                    </Link>
                  </>
                )}
              </div>
            }
            icon={User}
            avatarBg="bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20"
            badges={contact.is_primary ? [{ label: "Primary Contact", variant: "default", className: "bg-amber-500 text-white" }] : []}
            owner={contact.owner ? { id: contact.owner.id, name: `${contact.owner.first_name} ${contact.owner.last_name || ""}`.trim() } : null}
            createdOn={contact.created_on}
            onEdit={hasPermission("contact.update") ? () => setEditOpen(true) : undefined}
            onWhatsApp={phoneNum && hasPermission("whatsapp.send") ? () => setWaDialogOpen(true) : undefined}
            onEmail={contact.email ? () => window.open(`mailto:${contact.email}`) : undefined}
            onAddTask={() => setTaskDialogOpen(true)}
            hasWhatsApp={Boolean(phoneNum && hasPermission("whatsapp.send"))}
            hasEmail={Boolean(contact.email)}
            backUrl="/contacts"
          />
        }
        sidebar={
          <EntityInfoSidebar
            sections={sidebarSections}
          />
        }
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Edit Contact Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <form onSubmit={handleEditSubmit}>
            <DialogHeader>
              <DialogTitle>Edit Contact</DialogTitle>
              <DialogDescription>
                Update contact profile, phone numbers, and title.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4 text-xs sm:text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="edit_first" className="text-xs sm:text-sm font-semibold">First Name *</Label>
                  <Input
                    id="edit_first"
                    value={editForm.first_name}
                    onChange={(e) => setEditForm((p) => ({ ...p, first_name: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit_last" className="text-xs sm:text-sm font-semibold">Last Name</Label>
                  <Input
                    id="edit_last"
                    value={editForm.last_name}
                    onChange={(e) => setEditForm((p) => ({ ...p, last_name: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="edit_title" className="text-xs sm:text-sm font-semibold">Job Title / Designation</Label>
                <Input
                  id="edit_title"
                  value={editForm.job_title}
                  onChange={(e) => setEditForm((p) => ({ ...p, job_title: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="edit_email" className="text-xs sm:text-sm font-semibold">Email</Label>
                  <Input
                    id="edit_email"
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm((p) => ({ ...p, email: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit_mobile" className="text-xs sm:text-sm font-semibold">Mobile / WhatsApp</Label>
                  <Input
                    id="edit_mobile"
                    value={editForm.mobile}
                    onChange={(e) => setEditForm((p) => ({ ...p, mobile: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="edit_phone" className="text-xs sm:text-sm font-semibold">Office Phone</Label>
                <Input
                  id="edit_phone"
                  value={editForm.phone}
                  onChange={(e) => setEditForm((p) => ({ ...p, phone: e.target.value }))}
                />
              </div>

              <div className="flex items-center gap-2.5 pt-2">
                <input
                  type="checkbox"
                  id="edit_primary"
                  checked={editForm.is_primary}
                  onChange={(e) => setEditForm((p) => ({ ...p, is_primary: e.target.checked }))}
                  className="rounded border-input text-primary focus:ring-primary size-4"
                />
                <Label htmlFor="edit_primary" className="cursor-pointer font-semibold text-xs sm:text-sm">
                  Primary Contact for this Account
                </Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={editLoading}>
                {editLoading ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Chat Dialog */}
      {phoneNum && (
        <WhatsAppChatDialog
          open={waDialogOpen}
          onOpenChange={(v) => {
            setWaDialogOpen(v);
            if (!v) fetch360();
          }}
          phone={phoneNum}
          name={fullName}
          contactId={contact.id}
        />
      )}

      {/* Task Dialog */}
      <TaskDialog
        open={taskDialogOpen}
        onOpenChange={setTaskDialogOpen}
        defaultRelated={{
          type: "CONTACT",
          id: contact.id,
          name: fullName,
        }}
        onSaved={() => {
          setTaskDialogOpen(false);
          fetch360();
        }}
      />
    </>
  );
}
